import {useEffect,useRef,useState,type ChangeEvent,type DragEvent} from 'react';

type Props={actorId:string;displayName:string;editable?:boolean;
  lastLoginAt?:string|null;};
export function lastLoginLabel(value?:string|null,now=Date.now()):string|null{
  if(!value)return null;
  const elapsed=now-new Date(value).getTime();
  if(!Number.isFinite(elapsed)||elapsed<0)return null;
  const hours=Math.max(1,Math.floor(elapsed/3600000));
  if(hours<=24)return 'active '+hours+' hour'+(hours===1?'':'s')+' ago';
  const days=Math.floor(elapsed/86400000);
  if(days<=7)return 'active '+days+' day'+(days===1?'':'s')+' ago';
  if(days<=28){const weeks=Math.max(1,Math.floor(days/7));
    return 'active '+weeks+' week'+(weeks===1?'':'s')+' ago';}
  if(days<=365){const months=Math.max(1,Math.floor(days/30));
    return 'active '+months+' month'+(months===1?'':'s')+' ago';}
  const years=Math.floor(days/365);
  return 'active '+years+' year'+(years===1?'':'s')+' ago';
}

/** Shared optional photo field for staff profiles and signed-in guest /me.
 * The server verifies the signed-in user's actual account or staff access.
 */
export function ProfileAvatar({actorId,displayName,editable=true,lastLoginAt}:Props){
  const [source,setSource]=useState<string|null>(null);
  const [dragging,setDragging]=useState(false);
  const [status,setStatus]=useState('');
  const [revision,setRevision]=useState(0);
  const picker=useRef<HTMLInputElement|null>(null);

  useEffect(()=>{
    const controller=new AbortController();
    let localUrl:string|null=null;
    setSource(null);
    void fetch('/api/v1/profiles/'+encodeURIComponent(actorId)+'/avatar',{
      credentials:'same-origin',cache:'no-store',signal:controller.signal,
    }).then(async response=>{
      if(response.status===404)return;
      if(!response.ok)throw new Error('Photo unavailable');
      const data=await response.blob();
      if(controller.signal.aborted)return;
      localUrl=URL.createObjectURL(data);
      setSource(localUrl);
    }).catch(()=>{});
    return ()=>{controller.abort();if(localUrl)URL.revokeObjectURL(localUrl);};
  },[actorId,revision]);

  const submit=async(file:File|undefined)=>{
    if(!file)return;
    if(!['image/jpeg','image/png','image/webp'].includes(file.type)){
      setStatus('Choose a JPG, PNG or WebP photo.');return;
    }
    setStatus('Uploading photo…');
    let previewUrl:string|null=null;
    try{
      previewUrl=URL.createObjectURL(file);
      const img=new Image();
      img.src=previewUrl;
      await img.decode();
      const edge=320;
      const scale=Math.min(1,edge/Math.max(img.naturalWidth,img.naturalHeight));
      const canvas=document.createElement('canvas');
      canvas.width=Math.round(img.naturalWidth*scale);
      canvas.height=Math.round(img.naturalHeight*scale);
      const ctx=canvas.getContext('2d');
      if(!ctx)throw new Error('Unable to process photo.');
      ctx.drawImage(img,0,0,canvas.width,canvas.height);
      const blob=await new Promise<Blob|null>(resolve=>
        canvas.toBlob(resolve,'image/jpeg',0.78));
      if(!blob||blob.size>262144)
        throw new Error('Photo is too large. Choose a smaller picture.');
      const response=await fetch('/api/v1/profiles/'+encodeURIComponent(actorId)+'/avatar',{
        method:'PUT',credentials:'same-origin',cache:'no-store',
        headers:{'Content-Type':'image/jpeg'},body:blob,
      });
      if(!response.ok)throw new Error('Photo could not be saved.');
      setRevision(value=>value+1);
      window.dispatchEvent(new CustomEvent('ecfs-avatar-updated',{
        detail:{actorId},
      }));
      setStatus('Photo saved.');
    }catch(error){
      setStatus(error instanceof Error?error.message:'Photo could not be saved.');
    }finally{
      if(previewUrl)URL.revokeObjectURL(previewUrl);
    }
  };

  const onDrop=(event:DragEvent<HTMLButtonElement>)=>{
    event.preventDefault();setDragging(false);
    if(editable)void submit(event.dataTransfer.files[0]);
  };
  const portrait=<>
    {source?<img src={source} alt={displayName}/>:
      <svg className="ecfs-avatar-placeholder" viewBox="0 0 120 120"
        aria-hidden="true" fill="currentColor">
        <circle cx="60" cy="37" r="27"/>
        <path d="M4 118v-8c0-29 22-48 56-48s56 19 56 48v8Z"/>
      </svg>}
    {editable&&<span className="ecfs-avatar-drop-label">
      Drag and Drop a photo here</span>}
  </>;
  const activity=lastLoginLabel(lastLoginAt);
  return <div className="ecfs-avatar-upload" aria-label={displayName+' profile photo'}>
    {editable?<button type="button"
      className={'ecfs-record-avatar ecfs-avatar-interactive'+
        (dragging?' ecfs-avatar-dragging':'')}
      aria-label={'Upload photo for '+displayName}
      onClick={()=>picker.current?.click()}
      onDragOver={event=>{event.preventDefault();setDragging(true);}}
      onDragLeave={event=>{event.preventDefault();setDragging(false);}}
      onDrop={onDrop}>{portrait}</button>
      :<div className="ecfs-record-avatar" aria-label={source?'Profile photo':'No profile photo'}>
        {portrait}</div>}
    {editable&&<input ref={picker} type="file" accept="image/png,image/jpeg,image/webp"
      aria-label="Choose profile photo" className="ecfs-avatar-file"
      onChange={(event:ChangeEvent<HTMLInputElement>)=>{
        const file=event.target.files?.[0];event.target.value='';
        void submit(file);
      }}/>}
    {activity&&<small className="ecfs-avatar-activity">{activity}</small>}
    {status&&<small role="status" className="ecfs-avatar-status">{status}</small>}
  </div>;
}
